import { OverlayMenu, PrimeTable } from 'components';
import React, { useMemo, CSSProperties, useRef, useCallback, useState, useEffect } from 'react';
import { Card, CardBody, Container } from 'reactstrap';
import { Column, ColumnProps, ColumnBodyType } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { locationService } from './../../../services/LocationService';
import { useToasts } from 'app/components/Toast';
import { useRoleDatatable } from '../hooks/useRoleDatatable';
import { DataTable } from 'primereact/datatable';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useDeleteRoleConfigurationMutation } from '../services/roleApi';
import { RoleConfiguration } from '../types';
import { useAppDispatch } from 'app/store';
import { setIsEditingPage } from 'app/reducers/pageSlice';

interface Props {
	columns?: ColumnProps[];
}

const RoleManagementPage = (props: Props) => {
	
	const dispatch = useAppDispatch()

	useEffect(() => {
		dispatch(setIsEditingPage(false)) // disable editing state in order to show add menu button
	},[dispatch])

	const toast = useToasts();

	const { data, isLoading, totalRecords, rows, first, filters, sortField, sortOrder, onPage, onSort, onFilter } =
		useRoleDatatable();

	const [selectedRows, setSelectedRows] = useState(null);
	const [globalFilter] = useState(null);

	const dt = useRef<DataTable>(null);

	const history = locationService.getHistory();

	const [showDialog, setShowDialog] = useState(false);

	const [deleteRole] = useDeleteRoleConfigurationMutation();

	const templateFeature = () => {
		return (
			<React.Fragment>
				<i className="mdi mdi-file-find"></i>
			</React.Fragment>
		);
	};

	const templateTag = (rowData: RoleConfiguration): ColumnBodyType => {
		if (rowData.tag && rowData.tag.length > 0) {
			return rowData.tag
				.map((tag: any) => {
					return tag.tagName || '-';
				})
				.join(', ');
		} else {
			return '-';
		}
	};

	const _handleDelete = useCallback(
		(id: number | string) => {
			confirmDialog({
				message: `Do you want to delete this record with ID: ${id}?`,
				header: 'Delete Confirmation',
				icon: 'pi pi-info-circle',
				acceptClassName: 'p-button-danger',
				accept: async () =>
					deleteRole(id)
						.unwrap()
						.then(() => {
							toast.toastSuccess('Success', `Role Management deleted.`);
						})
						.catch((error) => {
							toast.toastError(error?.data?.error, error?.data?.message);
						}),
			});
		},
		[deleteRole, toast],
	);

	const _handleEdit = useCallback(
		(id: number | string) => {
			history.push({ pathname: `/role-management/edit/${id}` });
		},
		[history],
	);

	const columns: ColumnProps[] = useMemo(() => {
		const actionTemplateBody = (rowData: RoleConfiguration): ColumnBodyType => {
			const items = [
				{
					label: 'Edit',
					icon: 'mdi mdi-pencil',
					command: () => _handleEdit(rowData.id),
				},
				{
					label: 'Delete',
					icon: 'mdi mdi-delete',
					command: () => _handleDelete(rowData.id),
				},
			];
			return (
				<React.Fragment>
					<OverlayMenu id={`overlay-menu-${rowData.id}`} items={items} />
				</React.Fragment>
			);
		};

		return [
			{
				field: 'id',
				header: 'ID',
			},
			{
				field: 'name',
				header: 'NAME',
			},
			{
				field: 'tag',
				header: 'TAG',
				body: templateTag,
			},
			{
				field: 'description',
				header: 'DESCRIPTION',
			},
			{
				field: 'feature',
				header: 'FEATURE',
				body: templateFeature,
				headerStyle: {
					width: '8em',
					textAlign: 'center',
				} as CSSProperties,
				bodyStyle: {
					textAlign: 'center',
				} as CSSProperties,
			},

			{
				headerStyle: {
					width: '5em',
				} as CSSProperties,
				body: actionTemplateBody,
				bodyStyle: {
					textAlign: 'center',
					overflow: 'visible',
				} as CSSProperties,
			},
		];
	}, [_handleDelete, _handleEdit]);

	const CreateRole = () => {
		history.push({
			pathname: '/role-management/new',
		});
	};

	const renderHeader = () => {
		return (
			<div className="table-header">
				<span className="p-input-icon-left">
					<i className="pi pi-search" />
					<InputText type="search" placeholder="Search" />
				</span>

				<Button
					label="Add New"
					icon="mdi mdi-plus-circle"
					iconPos="right"
					className="p-button-link text-default"
					onClick={CreateRole}
				/>
			</div>
		);
	};

	const renderColumns = () => {
		return columns && columns.map((column: ColumnProps, i) => <Column key={i} {...column} />);
	};

	const header = renderHeader();

	return (
		<Container fluid>
			<h2 className="my-3">All Role</h2>
			<div>
				<Card>
					<CardBody>
						<PrimeTable
							lazy
							ref={dt}
							value={data}
							header={header}
							dataKey="id"
							globalFilter={globalFilter}
							selection={selectedRows}
							onSelectionChange={(e) => setSelectedRows(e.value)}
							rows={rows}
							first={first}
							sortField={sortField}
							sortOrder={sortOrder}
							loading={isLoading}
							totalRecords={totalRecords}
							filters={filters}
							onPage={onPage}
							onSort={onSort}
							onFilter={onFilter}
						>
							{renderColumns()}
						</PrimeTable>

						<ConfirmDialog visible={showDialog} onHide={() => setShowDialog(false)} />
					</CardBody>
				</Card>
			</div>
		</Container>
	);
};

export default RoleManagementPage;
